import React, { useCallback, useEffect, useMemo } from 'react';

import cn from 'classnames';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import {
  MdError,
  MdOutlineRemoveCircle,
  MdOutlineWarning,
  MdPersonAdd,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import {
  OrgPositionDetail,
  PersonAssigned,
} from '@appTypes/esb/organizations/positions/_orgGuid';
import {
  Positions,
  PositionsAllowedToHaveCR,
  PositionsRestrictedByPermissions,
  ProhibitedFunctionalPositions,
  ProhibitedUnitLeadershipPositions,
} from '@modules/permissionManager/constants';
import {
  PersonAssignedWithRegistration,
  RosterAdultMemberWitRegistration,
} from '@modules/permissionManager/types';
import { toastService } from '@modules/toasts';
import { Button, Card, PersonAvatar, S, Select } from '@shared/components';
import { hasNewPermissionSel } from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';

import styles from '../assignees.less';

interface PositionSelectorProps {
  positionData: OrgPositionDetail;
  unitUsers?: RosterAdultMemberWitRegistration[];
  loading?: boolean;
  isSaving?: boolean;
  disabled?: boolean;
  positionsWithMoreUsersThanMax?: OrgPositionDetail[];
  usersWhithoutRegistered?: string[];
  registeredPositions?: OrgPositionDetail[];
  functionalPositions?: OrgPositionDetail[];
  onAddRegistered: (PositionId: number, personGuid: string) => void;
  onRemoveRegistered: (PositionId: number, personGuid: string) => void;
  onAddFunctional: (PositionId: number, personGuid: string) => void;
  onRemoveFunctional: (
    PositionId: number,
    personGuid: string,
    PkPersonPositionId: number,
  ) => void;
  onError: (positionId: string, hasError: boolean, message: string) => void;
}

export const PositionSelector = ({
  positionData,
  unitUsers = [],
  loading = false,
  isSaving = false,
  disabled = false,
  positionsWithMoreUsersThanMax = [],
  usersWhithoutRegistered = [],
  registeredPositions = [],
  functionalPositions = [],
  onAddRegistered,
  onRemoveRegistered,
  onAddFunctional,
  onRemoveFunctional,
  onError,
}: PositionSelectorProps) => {
  const {
    PositionId,
    positionLong,
    max,
    min,
    personsAssigned = [],
    isYouthPosition,
    isFunctional,
  } = positionData;

  const hasFullPerms = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_LEADER),
  );

  const hasRestrictedPerms = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_LEADER_RESTRICTED),
  );

  const isExecOfficer = positionLong === Positions.EO;

  const getIsDisabled = useCallback(() => {
    if (disabled) return true;

    if (isExecOfficer) return true;

    if (hasFullPerms) return false;

    if (hasRestrictedPerms) {
      return PositionsRestrictedByPermissions.includes(positionLong);
    }

    return false;
  }, [disabled, hasRestrictedPerms, hasFullPerms, positionLong, isExecOfficer]);

  const getProhibitedUnitLeadershipPositions = useCallback(
    (extraPositions: string[] = []) => {
      const allPositions = registeredPositions.concat(functionalPositions);
      const allProhibitedPositions = [
        ...ProhibitedUnitLeadershipPositions,
        ...extraPositions,
      ];
      const prohibitedPositions = allPositions.filter(pos =>
        allProhibitedPositions.includes(pos.positionLong),
      );
      return prohibitedPositions;
    },
    [functionalPositions, registeredPositions],
  );

  const prohibitedFunctionalPositions = useMemo(() => {
    const prohibitedPositions = functionalPositions.filter(pos =>
      ProhibitedFunctionalPositions.includes(pos.positionLong),
    );

    return prohibitedPositions;
  }, [functionalPositions]);

  const getUserIsAlreadyAssigned = useCallback(
    (unitUser: RosterAdultMemberWitRegistration) => {
      const isAlreadyAssigned = !!personsAssigned.find(person => {
        // For functional, don't count them as assigned if they are lapsed
        if (isFunctional) {
          return person.personGuid === unitUser.personGuid && !person.islapsed;
        }
        return person.personGuid === unitUser.personGuid;
      });
      return isAlreadyAssigned;
    },
    [personsAssigned, isFunctional],
  );

  const getCanAssignPosition = useCallback(
    (unitUser: RosterAdultMemberWitRegistration) => {
      // Only for Den Leader position (FYI: DL is a registered position)
      if (Positions.DL === positionLong) {
        // The only prohibited position currently is Den Support
        const prohibitedPositions = functionalPositions.filter(
          pos => pos.positionLong === Positions.DS,
        );
        const hasUserProhibitedPosition = prohibitedPositions.some(pos =>
          pos.personsAssigned.find(
            person => person.personGuid === unitUser.personGuid,
          ),
        );
        if (hasUserProhibitedPosition) {
          toastService.error(
            <FormattedMessage id="permissionManager.assignees.denLeaderError" />,
          );
          return false;
        }
      }

      // This is for registered positions only
      if (!isFunctional && registeredPositions.length) {
        const userRegisteredPosiotions = registeredPositions.filter(regPos => {
          const foundUserAssigned = regPos.personsAssigned.find(
            person => person.personGuid === unitUser.personGuid,
          );

          // Check if the user has an active registered position
          const foundUserHasActiveRegistration = foundUserAssigned
            ? unitUser.registrations.some(
                registration =>
                  !registration.isFunctional && !registration.isExpired,
              )
            : false;

          // If they have an active registration, don't return the position if it's expired
          if (foundUserHasActiveRegistration) {
            return unitUser.registrations.filter(
              reg => reg.positionId === regPos.PositionId && !reg.isExpired,
            ).length;
          }

          // If they don't have an active registration, return all the positions found
          return foundUserAssigned;
        });

        // Users should only have 1 registered position, they could have 2 if the positions are a certain pairs, but no more.
        if (userRegisteredPosiotions.length >= 2) {
          toastService.error(
            <FormattedMessage id="permissionManager.assignees.maxRegisteredPositionsError" />,
          );
          return false;
        }

        // User doesn't have registered positions and is trying to assign some registered ones
        // while having functional ones that overlap in permissions
        if (
          !userRegisteredPosiotions.length &&
          ProhibitedUnitLeadershipPositions.includes(positionLong)
        ) {
          const hasUserProhibitedPosition = prohibitedFunctionalPositions.some(
            pos =>
              pos.personsAssigned.find(
                person => person.personGuid === unitUser.personGuid,
              ),
          );
          if (hasUserProhibitedPosition) {
            toastService.error(
              <FormattedMessage id="permissionManager.assignees.hasProhibitedFunctionalPositionError" />,
            );
            return false;
          }
        }

        // User doesn't have registered positions
        if (!userRegisteredPosiotions.length) return true;

        // At this point we know for sure the user has only 1 registered position.
        const isUserPositionCR =
          userRegisteredPosiotions[0].positionLong === Positions.CR;
        const isUserPositionAllowedToHaveCR = PositionsAllowedToHaveCR.includes(
          userRegisteredPosiotions[0].positionLong,
        );

        // Valid pair combinations of positions:
        // CR + CC
        // CR + MC
        // CR + NM
        // CR + PT
        // User has CR:
        if (isUserPositionCR) {
          const isAllowedToHaveCR =
            PositionsAllowedToHaveCR.includes(positionLong);
          if (!isAllowedToHaveCR) {
            toastService.error(
              <FormattedMessage id="permissionManager.assignees.notAllowedCRError" />,
            );
          }
          return isAllowedToHaveCR;
        }
        // Checks if user is trying to assign CR
        if (isUserPositionAllowedToHaveCR) {
          const isCR = positionLong === Positions.CR;
          if (!isCR) {
            toastService.error(
              <FormattedMessage id="permissionManager.assignees.maxRegisteredPositionsError" />,
            );
            return false;
          }

          const hasUserProhibitedPosition = prohibitedFunctionalPositions.some(
            pos =>
              pos.personsAssigned.find(
                person => person.personGuid === unitUser.personGuid,
              ),
          );
          if (isCR && hasUserProhibitedPosition) {
            toastService.error(
              <FormattedMessage id="permissionManager.assignees.hasProhibitedFunctionalPositionError" />,
            );
            return false;
          }

          return isCR;
        }

        toastService.error(
          <FormattedMessage id="permissionManager.assignees.maxRegisteredPositionsError" />,
        );
        return false;
      }

      // Executive Officers can not have functional positions
      if (isFunctional && registeredPositions.length) {
        const userhasExecutiveOfficerPositions = registeredPositions.find(
          regPos =>
            regPos.positionLong === Positions.EO &&
            regPos.personsAssigned.find(
              person => person.personGuid === unitUser.personGuid,
            ),
        );
        if (userhasExecutiveOfficerPositions) {
          toastService.error(
            <FormattedMessage id="permissionManager.assignees.executiveOfficerError" />,
          );
          return false;
        }
      }

      // Only for unit support position
      if (Positions.US === positionLong) {
        // Add Key 3 delegate to prohibited list
        const prohibitedPositions = getProhibitedUnitLeadershipPositions([
          Positions.K3D,
        ]);
        const hasUserProhibitedPosition = prohibitedPositions.some(pos =>
          pos.personsAssigned.find(
            person => person.personGuid === unitUser.personGuid,
          ),
        );
        if (hasUserProhibitedPosition) {
          toastService.error(
            <FormattedMessage id="permissionManager.assignees.hasUnitLeaderWithK3DError" />,
          );
        }

        return !hasUserProhibitedPosition;
      }

      // Only for sub-unit support or 3 key delgate position
      if (Positions.SUS === positionLong || Positions.K3D === positionLong) {
        const hasUserProhibitedPosition =
          getProhibitedUnitLeadershipPositions().some(pos =>
            pos.personsAssigned.find(
              person => person.personGuid === unitUser.personGuid,
            ),
          );

        if (hasUserProhibitedPosition) {
          toastService.error(
            <FormattedMessage id="permissionManager.assignees.hasUnitLeaderError" />,
          );
        }
        return !hasUserProhibitedPosition;
      }

      // Only for Den Support position
      if (Positions.DS === positionLong) {
        // Add Den Leader to prohibited list
        const prohibitedPositions = getProhibitedUnitLeadershipPositions([
          Positions.DL,
        ]);
        const hasUserProhibitedPosition = prohibitedPositions.some(pos =>
          pos.personsAssigned.find(
            person => person.personGuid === unitUser.personGuid,
          ),
        );
        if (hasUserProhibitedPosition) {
          toastService.error(
            <FormattedMessage id="permissionManager.assignees.hasUnitLeaderWithDLError" />,
          );
        }

        return !hasUserProhibitedPosition;
      }

      //Only for COR/CUR delegate
      if (Positions.CORD === positionLong) {
        const prohibitedPositions = registeredPositions.filter(pos =>
          ([Positions.CR, Positions.CUR] as string[]).includes(
            pos.positionLong,
          ),
        );
        const hasUserProhibitedPosition = prohibitedPositions.some(pos =>
          pos.personsAssigned.find(
            person => person.personGuid === unitUser.personGuid,
          ),
        );
        if (hasUserProhibitedPosition) {
          toastService.error(
            <FormattedMessage id="permissionManager.assignees.hasProhibitedCORCURError" />,
          );
        }

        return !hasUserProhibitedPosition;
      }

      return true;
    },
    [
      isFunctional,
      registeredPositions,
      functionalPositions,
      positionLong,
      prohibitedFunctionalPositions,
      getProhibitedUnitLeadershipPositions,
    ],
  );

  const hasActiveRegisteredPos = useCallback(
    (personAssigned: PersonAssignedWithRegistration) =>
      unitUsers.find(
        user =>
          user.personGuid === personAssigned.personGuid &&
          user.registrations.some(reg => !reg.isFunctional && !reg.isExpired),
      ),
    [unitUsers],
  );

  const getRegistrations = useCallback(
    (validPerson: PersonAssigned) => {
      const emtpyRegistration = {
        personFullName: '',
        position: '',
        positionId: 0,
        registrationExpireDt: '',
        isFunctional: true,
        isExpired: true,
      };

      let registration = emtpyRegistration;
      let mockRegistration = emtpyRegistration;

      const foundUser = unitUsers.find(
        user => validPerson.personGuid === user.personGuid,
      );

      if (foundUser?.registrations) {
        const foundRegistrationforPosition = foundUser.registrations.find(
          user => user.positionId === PositionId,
        );

        registration = foundRegistrationforPosition ?? emtpyRegistration;
      }

      if (foundUser?.mockRegistrations) {
        const foundRegistrationforPosition = foundUser.mockRegistrations.find(
          user => user.positionId === PositionId && !user.isExpired,
        );

        mockRegistration = foundRegistrationforPosition ?? emtpyRegistration;
      }

      return { registration, mockRegistration };
    },
    [unitUsers, PositionId],
  );

  const { lapsedList, activeList } = useMemo(() => {
    const lapsedList: PersonAssignedWithRegistration[] = [];
    const activeList: PersonAssignedWithRegistration[] = [];

    sortBy(personsAssigned, ['lastName', 'firstName']).forEach(person => {
      const { registration, mockRegistration } = getRegistrations(person);
      if (!mockRegistration.isExpired) {
        activeList.push({
          ...person,
          registration,
        });
        return;
      }
      //We show the lapsed list only for registered positions
      if (!isFunctional) {
        lapsedList.push({
          ...person,
          registration,
        });
      }
    });

    return { lapsedList, activeList };
  }, [personsAssigned, isFunctional, getRegistrations]);

  const hasNoMax = max > 99;
  const showRequiredMsg = min > 0 && !activeList.length;
  const showMinNotReachedMsg = activeList.length < min && !showRequiredMsg;
  const showMaxExeededMsg = activeList.length > max && !hasNoMax;
  const hasError = showRequiredMsg || showMinNotReachedMsg || showMaxExeededMsg;

  const showMaxOverflowToast = () => {
    const posNames = positionsWithMoreUsersThanMax
      .map(({ positionLong }) => positionLong)
      .toString()
      .replaceAll(',', ', ')
      .trim()
      .slice(0, -1);

    toastService.error(
      <FormattedMessage
        id="permissionManager.assignees.maxOverflowError"
        values={{ positions: posNames }}
      />,
    );
  };

  const handleChange = (personGuid: string) => {
    if (positionsWithMoreUsersThanMax.length) {
      showMaxOverflowToast();
      return;
    }

    const foundPerson = unitUsers.find(user => user.personGuid === personGuid);
    const canAssign = foundPerson ? getCanAssignPosition(foundPerson) : false;
    if (!canAssign) return;

    if (isFunctional) {
      onAddFunctional(PositionId, personGuid);
      return;
    }

    onAddRegistered(PositionId, personGuid);

    // If is any of these key 3 position we need to remove key 3 delegate
    if (ProhibitedUnitLeadershipPositions.includes(positionLong)) {
      // The only prohibited position currently is Den Support
      const prohibitedK3D = functionalPositions.find(
        pos => pos.positionLong === Positions.K3D,
      );
      const foundUserK3D = prohibitedK3D?.personsAssigned.find(
        person => person.personGuid === foundPerson?.personGuid,
      );
      if (foundUserK3D && prohibitedK3D && foundPerson) {
        onRemoveFunctional(
          prohibitedK3D.PositionId,
          personGuid,
          foundUserK3D.pkpersonPositionId,
        );
      }
    }

    // if is assigning COR or CUR remove COR/CUR Delegate if they have it
    if (Positions.CR === positionLong || Positions.CUR === positionLong) {
      // The only prohibited position currently is COR/CUR Delegate
      const prohibitedCorcurDelegate = functionalPositions.find(
        pos => pos.positionLong === Positions.CORD,
      );
      const foundUserDelegate = prohibitedCorcurDelegate?.personsAssigned.find(
        person => person.personGuid === foundPerson?.personGuid,
      );
      if (foundUserDelegate && prohibitedCorcurDelegate && foundPerson) {
        onRemoveFunctional(
          prohibitedCorcurDelegate.PositionId,
          personGuid,
          foundUserDelegate.pkpersonPositionId,
        );
      }
    }
  };

  const handleRemoveFromList =
    (currentPersonAssigned: PersonAssigned) => () => {
      if (
        positionsWithMoreUsersThanMax.length &&
        !positionsWithMoreUsersThanMax.find(
          pos => pos.PositionId === PositionId,
        )
      ) {
        showMaxOverflowToast();
        return;
      }

      const { personGuid, pkpersonPositionId } = currentPersonAssigned;
      if (isFunctional) {
        onRemoveFunctional(PositionId, personGuid, pkpersonPositionId);
        return;
      }

      onRemoveRegistered(PositionId, personGuid);
    };

  const getPictureUrl = useCallback(
    personGuid => {
      const foundUser = unitUsers.find(user => user.personGuid === personGuid);

      return foundUser ? foundUser.pictureUrl : '';
    },
    [unitUsers],
  );

  useEffect(() => {
    let message = '';
    if (showMaxExeededMsg) {
      message = `${positionLong} exceeds the number of members permited`;
    }

    if (showMinNotReachedMsg) {
      message = `${positionLong} needs at least ${min} members selected`;
    }

    if (showRequiredMsg) {
      message = `${positionLong} requires at least 1 member selected`;
    }

    onError(
      `${PositionId}`,
      showRequiredMsg || showMinNotReachedMsg || showMaxExeededMsg,
      message,
    );
  }, [
    min,
    positionLong,
    PositionId,
    showRequiredMsg,
    showMinNotReachedMsg,
    showMaxExeededMsg,
    onError,
  ]);

  const optionsSorted = useMemo(() => {
    const simpleSorted = sortBy(unitUsers, ['lastName', 'firstName']);
    if (!usersWhithoutRegistered.length) {
      return simpleSorted;
    }

    const warningOptions: RosterAdultMemberWitRegistration[] = [];
    const normalOptions: RosterAdultMemberWitRegistration[] = [];

    simpleSorted.forEach(user => {
      if (usersWhithoutRegistered.includes(user.personGuid)) {
        warningOptions.push(user);
        return;
      }
      normalOptions.push(user);
    });

    return [...warningOptions, ...normalOptions];
  }, [unitUsers, usersWhithoutRegistered]);

  return (
    <Card
      loading={loading}
      className={cn(styles.positionContainer, {
        [styles.cardError]: hasError && !loading,
      })}
    >
      <S size="4" colored semibold>
        <div className={styles.selectorTitleContainer}>
          <span>{positionLong}</span>
          <FormattedMessage
            id="permissionManager.assignees.personSelect.minMax"
            values={{ min, max: hasNoMax ? 'no max' : `${max} max` }}
          />
        </div>
      </S>
      {isExecOfficer ? (
        <Text color="color-primary" font="body-caption">
          <FormattedMessage id="permissionManager.assignees.execOfficerInstruction" />
        </Text>
      ) : (
        <Select
          size="large"
          rounded
          optionFilterProp="children"
          showSearch
          placeholder="Select a Member"
          disabled={getIsDisabled() || isSaving}
          onChange={handleChange}
          value={undefined}
        >
          {optionsSorted.map(user => {
            const { personFullName, personGuid, registrations = [] } = user;
            const hasCurrentActiveRegistration = !!registrations.find(
              reg => !reg.isFunctional && !reg.isExpired,
            );
            const userIsAlreadyAssigned = getUserIsAlreadyAssigned(user);
            let extraText = '';
            extraText = userIsAlreadyAssigned
              ? ' (Already Selected)'
              : extraText;
            extraText = !hasCurrentActiveRegistration
              ? ' (Expired)'
              : extraText;
            return (
              <Select.Option
                key={personGuid}
                value={personGuid}
                className={cn({
                  [styles.lapsedSelected]: !hasCurrentActiveRegistration,
                })}
                disabled={
                  !hasCurrentActiveRegistration || userIsAlreadyAssigned
                }
              >
                {`${personFullName}${extraText}`}
                {usersWhithoutRegistered.includes(personGuid) && (
                  <MdOutlineWarning color="#ffb708" />
                )}
              </Select.Option>
            );
          })}
        </Select>
      )}
      <div className={styles.memberdAddedSection}>
        <S size="6">
          <FormattedMessage id="permissionManager.assignees.personSelect.membersAdded" />
        </S>
        <S size="6">{`${activeList.length} of ${hasNoMax ? '99+' : max}`}</S>
      </div>
      <div className={styles.personListContainer}>
        {showMinNotReachedMsg && (
          <div className={styles.errorContainer}>
            <MdError />
            <S size="6">
              <FormattedMessage id="permissionManager.assignees.personSelect.minError" />
            </S>
          </div>
        )}
        {showMaxExeededMsg && (
          <div className={styles.errorContainer}>
            <MdError />
            <S size="6">
              <FormattedMessage id="permissionManager.assignees.personSelect.maxError" />
            </S>
          </div>
        )}
        {activeList.length ? (
          activeList.map(person => (
            <div
              key={`active-${PositionId}-${person.personGuid}`}
              className={styles.listItem}
            >
              <PersonAvatar
                className={styles.personIcon}
                bordered={false}
                pictureUrl={getPictureUrl(person.personGuid)}
                isAdult={!isYouthPosition}
                gender={person.gender}
              />
              <S size="5" className={cn(styles.personName)}>
                {person.fullName}
                {!hasActiveRegisteredPos(person) ? (
                  <Text font="body-caption" color="color-alerts-red">
                    <FormattedMessage id="permissionManager.assignees.expiredRegistration" />
                  </Text>
                ) : (
                  ''
                )}
              </S>
              <div className={styles.removeBtnContainer}>
                <Button
                  shape="circle"
                  type="ghost"
                  noBorder
                  size="small"
                  disabled={getIsDisabled()}
                  onClick={handleRemoveFromList(person)}
                >
                  <MdOutlineRemoveCircle />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div
            className={cn(styles.emptyMsgContainer, {
              [styles.withLapsedList]: lapsedList.length,
            })}
          >
            {showRequiredMsg && (
              <div className={styles.multipleErrorContainer}>
                <MdError />
                <S size="6">
                  <FormattedMessage id="permissionManager.assignees.personSelect.requiredError" />
                </S>
              </div>
            )}
            <MdPersonAdd />
            <S size="5" semibold>
              <FormattedMessage id="permissionManager.assignees.personSelect.noMembersAdded" />
            </S>
          </div>
        )}
        {!!lapsedList.length && (
          <div>
            <S size="6" className={styles.notCountedTitle}>
              <FormattedMessage id="permissionManager.assignees.personSelect.notCounted" />
            </S>
            <hr className={styles.listDivider} />
            {lapsedList.map(person => (
              <div
                key={`lapsed-${PositionId}-${person.personGuid}`}
                className={styles.listItem}
              >
                <PersonAvatar
                  className={styles.personIcon}
                  bordered={false}
                  isAdult={!isYouthPosition}
                  gender={person.gender}
                />
                <S
                  size="5"
                  className={cn(styles.personName, styles.lapsedListItem)}
                >
                  {`${person.fullName} (Term Expires${
                    person.registration.registrationExpireDt
                      ? `: ${DateTime.fromISO(
                          person.registration.registrationExpireDt,
                        ).toFormat('MM/dd/yy')}`
                      : ''
                  })`}
                </S>
                {isFunctional && (
                  <div className={styles.removeBtnContainer}>
                    <Button
                      shape="circle"
                      type="ghost"
                      noBorder
                      size="small"
                      disabled={getIsDisabled()}
                      onClick={handleRemoveFromList(person)}
                    >
                      <MdOutlineRemoveCircle />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
